import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ErreurApi, messageErreur } from '../../lib/api.ts';
import { Alerte, Bouton, Champ, ChampChoix, ChampTexte, EntetePage, Panneau, useNotifier } from '../../components/ui.tsx';
import { USAGES } from './PageSites.tsx';

export function PageNouveauSite() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.get<{ id: string; name: string }[]>('/api/clients') });
  const [f, setF] = useState({ name: '', clientName: '', address: '', city: '', usage: '', notes: '' });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const maj = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (!f.name.trim()) { setErreurs({ name: 'Indiquez le nom du site.' }); return; }
    setErreurs({}); setErreur(null); setEnvoi(true);
    try {
      const s = await api.post<{ id: string }>('/api/sites', { ...f, usage: f.usage || null });
      await qc.invalidateQueries({ queryKey: ['sites'] });
      notifier('Site créé.');
      nav(`/sites/${s.id}`);
    } catch (err) {
      if (err instanceof ErreurApi && Array.isArray(err.details)) {
        setErreurs(Object.fromEntries((err.details as any[]).map((d) => [d.champ, d.message])));
      }
      setErreur(messageErreur(err));
    } finally { setEnvoi(false); }
  };

  return (
    <div className="pile-l" style={{ maxWidth: 820 }}>
      <EntetePage titre="Nouveau site" sousTitre="Les bâtiments, étages, zones et équipements s’ajoutent ensuite depuis la fiche du site." />
      <form onSubmit={soumettre} noValidate>
        <Panneau>
          <div className="pile">
            {erreur && <Alerte ton="danger">{erreur}</Alerte>}
            <Champ libelle="Nom du site" required value={f.name} onChange={maj('name')} erreur={erreurs.name} placeholder="ex. Résidence Les Tilleuls, Siège social" />
            <div className="grille-form">
              <Champ libelle="Client" list="clients-connus" value={f.clientName} onChange={maj('clientName')} aide="Choisissez un client existant ou saisissez un nouveau nom." />
              <ChampChoix libelle="Usage" value={f.usage} onChange={maj('usage')} vide="Non précisé" options={Object.entries(USAGES).map(([valeur, libelle]) => ({ valeur, libelle }))} />
            </div>
            <datalist id="clients-connus">{clients.data?.map((c) => <option key={c.id} value={c.name} />)}</datalist>
            <div className="grille-form">
              <Champ libelle="Adresse" value={f.address} onChange={maj('address')} autoComplete="street-address" />
              <Champ libelle="Ville" value={f.city} onChange={maj('city')} autoComplete="address-level2" />
            </div>
            <ChampTexte libelle="Notes d’accès et consignes" value={f.notes} onChange={maj('notes')} aide="Horaires, contact sur place, local technique… Aucun mot de passe ici : utilisez le coffre du site." />
            <div className="barre-actions">
              <Bouton type="submit" variante="principal" chargement={envoi}>Créer le site</Bouton>
              <Bouton onClick={() => nav(-1)}>Annuler</Bouton>
            </div>
          </div>
        </Panneau>
      </form>
    </div>
  );
}
